import { noop } from 'lodash-es';
import React from 'react';
import styled, { css } from 'styled-components';

import { getHexFromProps, evaluateProp } from '../utils';

export const fjButtonTypes = {
  SM: 'small',
  MD: 'medium',
  LG: 'large',
  XL: 'extra-large',
  LINK: 'link',
  FORM: 'form',
  SQUARE: 'square',
} as const;

export type FjButtonType = (typeof fjButtonTypes)[keyof typeof fjButtonTypes];

interface ButtonStyle {
  color?: string | null;
  background?: string | null;
  borderColor?: string | null;
  textDecoration?: string;
}

interface StyledButtonProps {
  disabled?: boolean;
  $defaultStyle: ButtonStyle;
  $disabledStyle: ButtonStyle;
  $hoverStyle: ButtonStyle;
  width?: string | number;
  height?: string | number;
}

// styled-components generates a distinct component type for every element (a/button)
// and variant combination; the variant is chosen dynamically at render time, so the
// selected component is treated as a boundary here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ButtonVariant = React.ComponentType<any>;

const getColorFromProps =
  (attr: keyof ButtonStyle) =>
    (props: StyledButtonProps): string | null =>
      props.disabled
        ? getHexFromProps(props.$disabledStyle[attr] ?? undefined)
        : getHexFromProps(props.$defaultStyle[attr] ?? undefined);

const baseStyles = css<StyledButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-style: solid;
  border-width: 1px;
  border-radius: 3px;
  white-space: nowrap;
  text-decoration: none;
  box-sizing: border-box;
  font-size: 12px;
  background-color: ${(props) => getColorFromProps('background')(props)};
  color: ${(props) => getColorFromProps('color')(props)};
  border-color: ${(props) =>
    getColorFromProps('borderColor')(props) || getColorFromProps('color')(props)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  width: fit-content;
  min-width: ${(props) => evaluateProp(props.width ?? '') || '140px'};
  height: ${(props) => evaluateProp(props.height ?? '') || '40px'};
  padding: 0 10px 0 10px;

  &:hover {
    background-color: ${(props) =>
      getHexFromProps(props.$hoverStyle.background ?? undefined) ||
      getHexFromProps(props.$defaultStyle.color ?? undefined)};
    color: ${(props) =>
      getHexFromProps(props.$hoverStyle.color ?? undefined) ||
      getHexFromProps(props.$defaultStyle.background ?? undefined)};
    border-color: ${(props) =>
      getHexFromProps(props.$hoverStyle.borderColor ?? undefined) ||
      getHexFromProps(props.$defaultStyle.color ?? undefined)};
    transition: 0.2s ease;
    text-decoration: ${(props) => props.$hoverStyle.textDecoration || 'none'};
    cursor: pointer;
  }
`;

// Manual implementation of styled-component's "withComponent"
// withComponent can lose styles on nested styled() components.
// Every variant is built once at module load (see buttonVariants below) so styled-components
// never sees a styled() call during render — that fires the "created dynamically" warning even
// when the result is memoized.
const createButtonVariant = (
  shouldRenderLink: boolean,
  buttonType?: FjButtonType,
): ButtonVariant => {
  // The a/button element is chosen dynamically; styled.a and styled.button expose the same
  // template API, so we type the selected factory as one of them for the tagged-template call.
  const styledElementFunc = (shouldRenderLink ? styled.a : styled.button) as typeof styled.button;

  switch (buttonType) {
    case fjButtonTypes.SM:
      return styledElementFunc<StyledButtonProps>`
        ${baseStyles}
        height: ${(props) => evaluateProp(props.height ?? '') || '20px'};
        min-width: ${(props) => evaluateProp(props.width ?? '') || '70px'};
        font-size: 12px;
        margin: 10px;
      `;
    case fjButtonTypes.MD:
      return styledElementFunc<StyledButtonProps>`
        ${baseStyles}
        height: ${(props) => evaluateProp(props.height ?? '') || '30px'};
        min-width: ${(props) => evaluateProp(props.width ?? '') || '100px'};
        font-size: 12px;
      `;
    case fjButtonTypes.LG:
      return styledElementFunc<StyledButtonProps>`
        ${baseStyles}
        height: ${(props) => evaluateProp(props.height ?? '') || '40px'};
        min-width: ${(props) => evaluateProp(props.width ?? '') || '140px'};
        font-size: 14px;
        font-weight: bold;
      `;
    case fjButtonTypes.XL:
      return styledElementFunc<StyledButtonProps>`
        ${baseStyles}
        height: ${(props) => evaluateProp(props.height ?? '') || '50'};
        min-width: ${(props) => evaluateProp(props.width ?? '') || '200px'};
        font-size: 18px;
        font-weight: normal;
        margin: 40px 0px;

        &:first-child {
          margin-top: 0px;
        }

        &:last-child {
          margin-bottom: 0px;
        }

        a,
        button {
          border-radius: 5px;
        }
      `;
    case fjButtonTypes.FORM:
      return styledElementFunc<StyledButtonProps>`
        ${baseStyles}
        height: ${(props) => evaluateProp(props.height ?? '') || '30px'};
        min-width: ${(props) => evaluateProp(props.width ?? '') || '0px'};
        font-size: 12px;
        padding: 5px;
      `;
    case fjButtonTypes.LINK:
      // The LINK variant always renders as an anchor, so `target` is a valid attr here.
      return (styledElementFunc as typeof styled.a).attrs({
        target: '_blank',
      })<StyledButtonProps>`
        ${baseStyles}
        font-size: inherit;
        background-color: transparent;
        color: ${getColorFromProps('color')};
        border: 0;
        min-width: auto;
        height: auto;
        margin: 10px;

        &:hover {
          background-color: transparent;
          color: ${getColorFromProps('color')};
          border: 0;
          text-decoration: underline;
        }
      `;
    case fjButtonTypes.SQUARE:
      return styledElementFunc<StyledButtonProps>`
        ${baseStyles}
        height: ${(props) => evaluateProp(props.height ?? '') || '15px'};
        min-width: ${(props) => evaluateProp(props.width ?? '') || '15px'};
      `;
    default:
      return styledElementFunc<StyledButtonProps>`
        ${baseStyles}
      `;
  }
};

const variantKey = (shouldRenderLink: boolean, buttonType?: FjButtonType): string =>
  `${shouldRenderLink ? 'a' : 'button'}::${buttonType ?? 'default'}`;

const buttonVariants = ((): Map<string, ButtonVariant> => {
  const cache = new Map<string, ButtonVariant>();
  const types: (FjButtonType | undefined)[] = [...Object.values(fjButtonTypes), undefined];
  for (const buttonType of types) {
    for (const shouldRenderLink of [false, true]) {
      cache.set(
        variantKey(shouldRenderLink, buttonType),
        createButtonVariant(shouldRenderLink, buttonType),
      );
    }
  }
  return cache;
})();

const getButtonType = (
  shouldRenderLink: boolean,
  buttonType?: FjButtonType,
): ButtonVariant | undefined => buttonVariants.get(variantKey(shouldRenderLink, buttonType));

interface FjButtonProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'onClick'> {
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  disabledStyle?: ButtonStyle;
  defaultStyle?: ButtonStyle;
  hoverStyle?: ButtonStyle;
  buttonType?: FjButtonType;
  disabled?: boolean;
  width?: string | number;
  height?: string | number;
}

const FjButton = ({
  onClick = noop,
  disabledStyle = { color: '#CCCCCC', background: '#F7F7F7', borderColor: null },
  defaultStyle = { color: '#156777', background: '#FFFFFF', borderColor: null },
  hoverStyle = { color: null, background: null, borderColor: null },
  buttonType,
  ...rest
}: FjButtonProps) => {
  const shouldRenderLink = !!rest.href || buttonType === fjButtonTypes.LINK;
  const Button = getButtonType(shouldRenderLink, buttonType)!;
  return (
    <Button
      onClick={onClick}
      $disabledStyle={disabledStyle}
      $defaultStyle={defaultStyle}
      $hoverStyle={hoverStyle}
      {...rest}
    />
  );
};

export default FjButton;
